import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps,
  TextFieldProps,
  FileFieldProps,
} from '@/components/form-builder/form-builder.types';
import { CommitteeRepository } from '@/store/committee-state/committee-state.repository';
import { combineLatest, map, Observable, of } from 'rxjs';

interface getCommitteeFormObjectProps {
  store: CommitteeRepository;
  comTypeOptions: Observable<SelectOption[]>;
  tutelleOptions: Observable<SelectOption[]>;
  paysOptions: SelectOption[];
  memberOptions?: Observable<SelectOption[]>;
  isInspectMode?: boolean;
  downloadHandler?: (alfrescoId: string, fileName?: string, fileExtension?: string) => void;
  isDetailsMode?: boolean;
}

export const getCommitteeFormObject = ({
  store,
  comTypeOptions,
  tutelleOptions,
  paysOptions,
  memberOptions,
  isInspectMode = false,
  downloadHandler,
  isDetailsMode = false,
}: getCommitteeFormObjectProps): DynamicForm => {
  // Create stable observables that don't get recreated
  const stableIsDisabled = store.getNestedObservable<boolean>('isDisabled', 'nom');
  const stableTutelIdIsDisabled = store.getNestedObservable<SelectOption>('comTypeIdSelected').pipe(map(comType => !comType?.code));
  const stableSpecialIsDisabled = store.getNestedObservable<boolean>('isDisabled', 'special');
  const stableComTypeIdIsDisabled = store.getNestedObservable<boolean>('isDisabled', 'comTypeId');
  const stableTheAdresseIsDisabled = store.getNestedObservable<boolean>('isDisabled', 'theAdresse');
  const stableFilesIsDisabled = store.getNestedObservable<boolean>('isDisabled', 'files');

  // Create stable value observables
  const stableTuttelSelected = store.getObservable<SelectOption>('tuttelSelected');
  const stableSpecialValue = store.getNestedObservable<boolean>('dto', 'special');
  const stableComTypeIdSelected = store.getObservable<SelectOption>('comTypeIdSelected');

  const nomColumn: DynamicField<TextFieldProps> = {
    label: 'settings.committee.form.name.label',
    variant: FieldVariant.TEXT,
    isDisabled: stableIsDisabled,
    isRequired: true,
    error: combineLatest([store.getObservable('nomTouched'), store.getObservable('formSubmitted'), store.getNestedObservable('dto', 'nom')]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'settings.committee.form.name.error.required';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        store.setNested('dto', 'nom', value);
      },
      onBlur: () => store.set('nomTouched', true),
    },
  };

  const abreviationColumn: DynamicField<TextFieldProps> = {
    label: 'settings.committee.form.abreviation.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'abreviation'),
    isRequired: false,
    error: store.getNestedObservable<string>('errors', 'abreviation'),
    props: {
      value: store.getNestedObservable<string>('dto', 'abreviation'),
      onChange: (value: string) => {
        store.setNested('dto', 'abreviation', value);
      },
    },
  };

  const nomArColumn: DynamicField<TextFieldProps> = {
    label: 'settings.committee.form.nomAr.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'nomAr'),
    isRequired: false,
    error: store.getNestedObservable<string>('errors', 'nomAr'),
    props: {
      value: store.getNestedObservable<string>('dto', 'nomAr'),
      onChange: (value: string) => {
        store.setNested('dto', 'nomAr', value);
      },
    },
  };

  const tutelIdColumn: DynamicField<SelectFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.tutel.label',
    variant: FieldVariant.SELECT,
    isDisabled: stableTutelIdIsDisabled,
    isRequired: false,
    props: {
      placeholder: 'settings.contractingAuthority.ac.form.tutel.placeholder',
      options: tutelleOptions,
      value: stableTuttelSelected,
      filterEnabled: true,
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'tutelId', value.code);
        store.set('tuttelSelected', value);
      },
      onBlur: () => store.set('tutelIdTouched', true),
    },
  };

  const decretColumn: DynamicField<TextFieldProps> = {
    label: 'settings.committee.form.decretCreation.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'decretCreation'),
    isRequired: true,
    error: combineLatest([
      store.getObservable('decretCreationTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'decretCreation'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'settings.committee.form.decretCreation.error.required';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable<string>('dto', 'decretCreation'),
      onChange: (value: string) => {
        store.setNested('dto', 'decretCreation', value);
      },
      onBlur: () => store.set('decretCreationTouched', true),
    },
  };

  const typeColumn: DynamicField<SelectFieldProps> = {
    label: 'settings.committee.form.committeeType.label',
    variant: FieldVariant.SELECT,
    isDisabled: stableComTypeIdIsDisabled,
    isRequired: true,
    error: combineLatest([
      store.getObservable('comTypeIdTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'comTypeId'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'settings.committee.form.committeeType.error.required';
        return '';
      })
    ),
    props: {
      options: comTypeOptions, // Use static options for complete stability
      value: stableComTypeIdSelected,
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'comTypeId', value.code);
        store.set('comTypeIdSelected', value);
      },
      onBlur: () => store.set('comTypeIdTouched', true),
    },
  };

  // Address fields
  const adresseFieldsRow1: DynamicField<TextFieldProps>[] = [
    {
      label: 'settings.contractingAuthority.ac.form.adresse.label',
      variant: FieldVariant.TEXT,
      isRequired: false,
      isDisabled: stableTheAdresseIsDisabled,
      props: {
        value: store.getNestedObservable<string>('dto', 'theAdresse', 'adresse'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).theAdresse || {};
          const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, adresse: value, id: adresseRaw.id } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.compAdresse.label',
      variant: FieldVariant.TEXT,
      isRequired: false,
      isDisabled: stableTheAdresseIsDisabled,
      props: {
        value: store.getNestedObservable<string>('dto', 'theAdresse', 'compAdresse'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).theAdresse || {};
          const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, compAdresse: value, id: adresseRaw.id } };
          store.set('dto', dto);
        },
      },
    },
  ];

  const emailField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.email.label',
    variant: FieldVariant.EMAIL,
    isRequired: true,
    isDisabled: stableTheAdresseIsDisabled,
    error: combineLatest([
      store.getNestedObservable<string>('errors', 'theAdresse', 'email'),
      store.getNestedObservable<string>('dto', 'theAdresse', 'email'),
      store.getObservable('theAdresseTouched'),
      store.getObservable('formSubmitted'),
    ]).pipe(
      map(([serverError, email, touched, submitted]) => {
        if (serverError) return serverError;
        if ((touched || submitted) && !email) return 'settings.contractingAuthority.ac.form.email.error.required';
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'settings.contractingAuthority.ac.form.email.error.invalid';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable<string>('dto', 'theAdresse', 'email'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).theAdresse || {};
        const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, email: value, id: adresseRaw.id } };
        store.set('dto', dto);
      },
      onBlur: () => {
        store.set('theAdresseTouched', true);
      },
    },
  };

  const adresseFieldsRow2: DynamicField<TextFieldProps>[] = [
    {
      label: 'settings.contractingAuthority.ac.form.tel1.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: stableTheAdresseIsDisabled,
      props: {
        value: store.getNestedObservable<string>('dto', 'theAdresse', 'tel1'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).theAdresse || {};
          const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, tel1: value, id: adresseRaw.id } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.tel2.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: stableTheAdresseIsDisabled,
      props: {
        value: store.getNestedObservable<string>('dto', 'theAdresse', 'tel2'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).theAdresse || {};
          const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, tel2: value, id: adresseRaw.id } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.fax.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: stableTheAdresseIsDisabled,
      props: {
        value: store.getNestedObservable<string>('dto', 'theAdresse', 'fax'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).theAdresse || {};
          const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, fax: value, id: adresseRaw.id } };
          store.set('dto', dto);
        },
      },
    },
  ];

  const codePostalField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.codePostal.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: stableTheAdresseIsDisabled,
    error: combineLatest([
      store.getNestedObservable<string>('errors', 'theAdresse', 'codePostal'),
      store.getNestedObservable<string>('dto', 'theAdresse', 'codePostal'),
    ]).pipe(
      map(([serverError, codePostal]) => {
        if (serverError) return serverError;
        if (codePostal && !/^\d+$/.test(codePostal)) return 'Code postal doit être numérique';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable<string>('dto', 'theAdresse', 'codePostal'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).theAdresse || {};
        const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, codePostal: value, id: adresseRaw.id } };
        store.set('dto', dto);
      },
    },
  };
  const villeField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.ville.label',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled: stableTheAdresseIsDisabled,
    props: {
      value: store.getNestedObservable<string>('dto', 'theAdresse', 'ville'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).theAdresse || {};
        const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, ville: value, id: adresseRaw.id } };
        store.set('dto', dto);
      },
    },
  };
  const paysField: DynamicField<SelectFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.pays.label',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled: stableTheAdresseIsDisabled,
    props: {
      placeholder: 'settings.contractingAuthority.ac.form.pays.placeholder',
      options: paysOptions,
      value: store.getObservable('paysSelected'),
      onSelectChange: (value: SelectOption) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).theAdresse || {};
        const dto = { ...dtoRaw, theAdresse: { ...adresseRaw, paysId: value.code, id: adresseRaw.id } };
        store.set('dto', dto);
        store.set('paysSelected', value);
      },
    },
  };

  const adresseFieldsRow3: DynamicField<TextFieldProps | SelectFieldProps>[] = [codePostalField, villeField];
  if (isDetailsMode) {
    adresseFieldsRow3.push(paysField);
  }

  const responsableColumn: DynamicField<SelectFieldProps> = {
    label: 'settings.committee.form.responsable.label',
    variant: FieldVariant.SELECT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'responsable'),
    isRequired: true,
    props: {
      options: memberOptions,
      value: store.getNestedObservable('memberSelected'),
      filterEnabled: true,
      onSelectChange: (value: SelectOption) => {
        store.setNested('memberSelected', value);
        store.setNested('dto', 'responsableId', value.code);
      },
      placeholder: 'Selectionnez un responsable',
    },
  };

  const specialColumn: DynamicField<SwitchFieldProps> = {
    label: 'settings.committee.form.special.label',
    variant: FieldVariant.SWITCH,
    isRequired: false,
    isDisabled: stableSpecialIsDisabled,
    props: {
      checked: stableSpecialValue,
      onChange: (value: any) => {
        if (value.checked === true) store.setNested('dto', 'special', 'true');
        else store.setNested('dto', 'special', 'false');
      },
      placeholder: 'settings.committee.form.special.placeholder',
    },
  };

  const filesColumn: DynamicField<FileFieldProps> = {
    label: 'settings.committee.form.files.label',
    variant: FieldVariant.FILE,
    isRequired: false,
    isDisabled: stableFilesIsDisabled,
    props: {
      mode: 'advanced',
      accept: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar',
      maxFileSize: 31457280,
      multiple: true,
      value: of([]),
      onFileSelect: (event: any) => {
        // Store the actual File objects in the DTO
        const dtoRaw = store.get('dto') || {};
        const fileList = event.files || [];
        // Convert FileList to Array of File objects
        const files = Array.from(fileList) as File[];
        const dto = { ...dtoRaw, files: files };
        store.set('dto', dto);
      },
      onFileRemoved: (event: any) => {
        // Handle removal of new files only
        const dtoRaw = store.get('dto') || {};
        const currentFiles = (dtoRaw as any).files || [];
        const updatedFiles = currentFiles.filter((file: any) => file.name !== event.file.name);
        const dto = { ...dtoRaw, files: updatedFiles };
        store.set('dto', dto);
      },
      showUploadButton: !isInspectMode,
      chooseLabel: 'settings.committee.form.files.choose',
      cancelLabel: 'settings.committee.form.files.cancel',
      existingLabel: 'settings.committee.form.files.existing',
      existingFileHeader: true,
      files: store.getNestedObservable('dto', 'docs').pipe(
        map((docs: any) => {
          const docsArray = Array.isArray(docs) ? docs : [];
          if (docsArray.length === 0) return [];
          const mappedFiles = docsArray.map((doc: any) => ({
            id: doc.id,
            alfresco_id: doc.alfresco_id,
            filename: doc.name,
            size: parseInt(doc.fileSize) || 0,
            extension: doc.fileExtension,
          }));
          return mappedFiles;
        })
      ),
      onFileRepresentationChange: (file: any) => {
        // Handle removal of existing documents
        const dtoRaw = store.get('dto') || {};
        const currentDocs = (dtoRaw as any).docs || [];
        const updatedDocs = currentDocs.filter((doc: any) => doc.id !== file.id);
        const dto = { ...dtoRaw, docs: updatedDocs };
        store.set('dto', dto);
      },
      onFileDownLoad: (event: any) => {
        // The alfresco_id is directly in the event object, not nested under file
        const alfrescoId = event?.alfresco_id;
        const fileName = event?.filename;
        const fileExtension = event?.extension;

        if (alfrescoId && downloadHandler) {
          // Pass file information to the download handler
          (downloadHandler as any)(alfrescoId, fileName, fileExtension);
        }
      },
      onAllRemoved: () => {
        // Clear both files and docs arrays when all files are removed
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, files: [], docs: [] };
        store.set('dto', dto);
      },
    },
  };

  return {
    title: 'Commission',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [nomColumn, nomArColumn, abreviationColumn],
          },
          {
            fields: [typeColumn, tutelIdColumn],
          },
          {
            fields: [emailField, decretColumn],
          },
          {
            fields: [filesColumn],
          },
          {
            fields: [responsableColumn, specialColumn],
          },
          {
            fields: adresseFieldsRow1,
          },
          {
            fields: adresseFieldsRow2,
          },
          {
            fields: adresseFieldsRow3,
          },
        ],
      },
    ],
  };
};
